import React, { useState } from 'react';
import { toast } from 'sonner';
import { CalendarClock, Loader2, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { fmtDate } from '../../lib/dates';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { es } from 'date-fns/locale';

// AI-proposed response deadline (Chilean business days/holidays) that the user confirms; email reminder 2 days before
export const DisciplinaryDeadline = ({ matterId, a, onSaved }) => {
  const { api } = useAuth();
  const prop = a.deadline_proposed;
  const [due, setDue] = useState(a.deadline?.due_date || prop?.due_date || '');
  const [email, setEmail] = useState(a.deadline?.notify_email || '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!due) return toast.error('Indica la fecha límite');
    setBusy(true);
    try { const r = await api.put(`/disciplinary/matters/${matterId}/deadline`, { due_date: due, notify_email: email.trim() || null }); onSaved(r.data.deadline); toast.success('Plazo confirmado · te avisaremos 2 días antes'); }
    catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : Array.isArray(d) && d.some((x) => x.loc?.includes('notify_email')) ? 'El correo adicional no es válido' : 'Revisa la fecha límite');
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="disc-deadline-box" className="rounded-xl border border-sky-500/30 bg-sky-500/5 p-4">
      <p className="flex items-center gap-2 text-sm font-semibold text-sky-200"><CalendarClock className="h-4 w-4" />Fecha límite para responder</p>
      {prop && (
        <p data-testid="disc-deadline-proposed" className="mt-1 text-xs text-slate-300">
          Cerebro propone el <b>{fmtDate(prop.due_date)}</b>: {prop.days} días {prop.day_type === 'habiles' ? 'hábiles' : 'corridos'} desde el {fmtDate(prop.from)}
          {prop.from_assumed_today ? ' (el texto no indica la fecha de notificación; se tomó hoy, corrígela si corresponde)' : ''}
          {prop.skipped_holidays?.length ? ` · descuenta feriados: ${prop.skipped_holidays.join(', ')}` : ''}.
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div className="text-[11px] text-slate-400">Fecha límite
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="disc-deadline-date" className="mt-1 flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-100"><CalendarClock className="h-3.5 w-3.5 text-sky-300" />{due ? fmtDate(due) : 'Elegir fecha'}</button>
            </PopoverTrigger>
            <PopoverContent className="w-auto border-slate-700 bg-slate-900 p-0" align="start">
              <Calendar locale={es} mode="single" selected={due ? new Date(`${due}T12:00:00`) : undefined} disabled={{ before: new Date() }}
                onSelect={(d) => d && setDue(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`)} />
            </PopoverContent>
          </Popover>
        </div>
        <label className="min-w-[200px] flex-1 text-[11px] text-slate-400">Correo adicional para el aviso (opcional)<input data-testid="disc-deadline-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="notaria@ejemplo.cl" className="mt-1 block w-full rounded-lg border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100" /></label>
        <button data-testid="disc-deadline-save" onClick={save} disabled={busy} className="flex items-center gap-1 rounded-lg bg-sky-500 px-4 py-2 text-xs font-semibold text-slate-950 disabled:opacity-60">{busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}{a.deadline ? 'Actualizar' : 'Confirmar fecha'}</button>
      </div>
      {a.deadline && (
        <p data-testid="disc-deadline-confirmed" className="mt-2 flex items-center gap-1 text-[11px] text-emerald-300"><CheckCircle2 className="h-3.5 w-3.5" />Confirmado: vence el {fmtDate(a.deadline.due_date)}. Aviso por correo 2 días antes a ti{a.deadline.notify_email ? ` y a ${a.deadline.notify_email}` : ''}{a.deadline.reminded_at ? ' · aviso enviado' : ''}.</p>
      )}
    </div>
  );
};
